import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Lock, Mail, User2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { BrandLogo } from '../components/layout/BrandLogo';

function validatePassword(password) {
  if (password.length < 8) return 'Use at least 8 characters.';
  if (!/[A-Z]/.test(password)) return 'Include at least one uppercase letter.';
  if (!/[a-z]/.test(password)) return 'Include at least one lowercase letter.';
  if (!/\d/.test(password)) return 'Include at least one number.';
  return '';
}

export default function SignupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { registerWithPassword, authError, clearError } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const passwordHint = validatePassword(password);
  const redirectTo = location.state?.from?.pathname || '/library';

  const handleSubmit = async (event) => {
    event.preventDefault();
    clearError();

    if (passwordHint) {
      toast.error(passwordHint);
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    const { error } = await registerWithPassword(name.trim(), email.trim(), password);
    setIsLoading(false);

    if (!error) {
      toast.success('Account created successfully.');
      navigate(redirectTo, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#fffdf9_0%,#f8efe6_45%,#fdf8f1_100%)] px-4 py-6 md:px-6 md:py-8">
      <div className="mx-auto max-w-6xl">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 rounded-full border border-[#E9DDD1] bg-white/90 px-4 py-2 text-sm font-medium text-[#2D241E]"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <div className="mt-6 grid overflow-hidden rounded-[36px] border border-[#E7DED3] bg-[#FFF9F4] shadow-[0_35px_120px_rgba(48,35,24,0.18)] md:grid-cols-[1.02fr_0.98fr]">
          <div className="hidden border-r border-[#ECE2D7] bg-[radial-gradient(circle_at_top_left,rgba(244,227,197,0.55),transparent_34%),linear-gradient(160deg,#F8F0E8_0%,#FBF7F2_45%,#F5EBE2_100%)] p-10 md:flex md:flex-col">
            <BrandLogo
              iconSize={36}
              titleClassName="text-2xl text-[#221B17]"
              subtitleClassName="text-xs tracking-[0.24em] text-[#8B8379]"
            />
            <h1 className="mt-16 max-w-sm font-serif text-5xl leading-[1.02] tracking-[-0.04em] text-[#221B17]">
              Create your secure invitation account.
            </h1>
            <p className="mt-5 max-w-md text-base leading-7 text-[#6F675F]">
              Save polished invitation directions, protect your export flow, and keep every revision attached to a real studio profile.
            </p>
          </div>

          <div className="px-5 py-8 md:px-12 md:py-12">
            <div className="md:hidden">
              <BrandLogo
                iconSize={36}
                titleClassName="text-2xl text-[#221B17]"
                subtitleClassName="text-xs text-[#8B8379]"
              />
            </div>

            <div className="mt-8 text-center md:mt-0 md:text-left">
              <div className="text-xs uppercase tracking-[0.24em] text-[#A2968A]">Studio access</div>
              <h2 className="mt-3 font-serif text-4xl leading-none tracking-[-0.04em] text-[#221B17]">
                Create your account
              </h2>
              <p className="mt-4 text-sm leading-7 text-[#7E766F]">
                This route uses the live password-based registration endpoint rather than local placeholder auth.
              </p>
            </div>

            {authError ? (
              <div className="mt-6 rounded-[22px] border border-[#F2C7C2] bg-[#FFF4F2] px-4 py-4 text-sm text-[#A94D45]">
                {authError}
              </div>
            ) : null}

            <form
              className="mt-8 rounded-[28px] border border-[#ECE2D7] bg-white p-4 shadow-[0_20px_60px_rgba(77,59,41,0.07)]"
              onSubmit={handleSubmit}
            >
              <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-[#8E857A]">
                Full name
              </label>
              <div className="relative">
                <User2 size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A79C90]" />
                <input
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Your full name"
                  className="w-full rounded-[22px] border border-[#E9E0D5] bg-[#FCFAF8] py-4 pl-12 pr-4 text-sm text-[#221B17] outline-none transition focus:border-[#D5C0A3] focus:bg-white"
                  disabled={isLoading}
                  required
                />
              </div>

              <label className="mb-2 mt-4 block text-xs uppercase tracking-[0.18em] text-[#8E857A]">
                Email address
              </label>
              <div className="relative">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A79C90]" />
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-[22px] border border-[#E9E0D5] bg-[#FCFAF8] py-4 pl-12 pr-4 text-sm text-[#221B17] outline-none transition focus:border-[#D5C0A3] focus:bg-white"
                  disabled={isLoading}
                  required
                />
              </div>

              <label className="mb-2 mt-4 block text-xs uppercase tracking-[0.18em] text-[#8E857A]">
                Password
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A79C90]" />
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Create a secure password"
                  className="w-full rounded-[22px] border border-[#E9E0D5] bg-[#FCFAF8] py-4 pl-12 pr-4 text-sm text-[#221B17] outline-none transition focus:border-[#D5C0A3] focus:bg-white"
                  disabled={isLoading}
                  required
                />
              </div>

              <p className={`mt-2 text-xs ${passwordHint ? 'text-[#A94D45]' : 'text-[#7E766F]'}`}>
                {passwordHint || 'Use 8+ characters with uppercase, lowercase, and a number.'}
              </p>

              <label className="mb-2 mt-4 block text-xs uppercase tracking-[0.18em] text-[#8E857A]">
                Confirm password
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A79C90]" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder="Re-enter your password"
                  className="w-full rounded-[22px] border border-[#E9E0D5] bg-[#FCFAF8] py-4 pl-12 pr-4 text-sm text-[#221B17] outline-none transition focus:border-[#D5C0A3] focus:bg-white"
                  disabled={isLoading}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || !name.trim() || !email.trim() || !password.trim() || Boolean(passwordHint) || password !== confirmPassword}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-[22px] bg-[#1F1A17] px-5 py-4 text-sm font-medium text-white transition hover:bg-[#2B2420] disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Creating account...
                  </>
                ) : (
                  'Create account'
                )}
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-[#7E746B] md:text-left">
              Already have an account?{' '}
              <Link className="underline-offset-4 hover:underline" state={{ from: { pathname: redirectTo } }} to="/login">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
